import styled, { css } from 'styled-components';
import { Swiper } from 'swiper/react';

import { SectionHeader } from '@/components/SectionHeader';
import { px2rem } from '@/theme';

import { Accordion } from '../../Accordion';
import { Box } from '../../Box';
import { Container } from '../../Container';
import { Flex } from '../../Flex';
import { NextImageWithLoader } from '../../NextImageWithLoader';

import DesktopCenterRulerImage from '@/assets/LineCenterDesktop.svg';
import MobileCenterRulerImage from '@/assets/LineCenterMobile.svg';
import MobileRulerImage from '@/assets/ruler.png';
import DesktopRulerImage from '@/assets/ruler-desktop.png';
export const CategoryWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  ${({ theme }) => css`
    padding: 0;
    gap: ${theme.space.s100};
    ${theme.media.md.up} {
      flex-wrap: nowrap;
      margin: ${theme.spacing(4, 0)};
      padding: 0 16px;
      gap: ${theme.space.s100};
      overflow-x: scroll;
      scrollbar-width: none; /* For Firefox */
      -ms-overflow-style: none; /* For Internet Explorer and Edge */
      &::-webkit-scrollbar {
        display: none;
      }
    }

    @media (min-width: 1280px) {
      padding: 1rem;
      margin: 0;
    }
  `}
`;
export const BannerCategoryWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  text-align: center;
  justify-content: start;

  ${({
    theme: {
      breakpoints,
      colors,
      components: {
        header: { breakpoint },
      },
    },
  }) => css`
    background-color: ${colors.translucentBlack};
    overflow-x: scroll;
    scrollbar-width: none; /* For Firefox */
    -ms-overflow-style: none; /* For Internet Explorer and Edge */
    &::-webkit-scrollbar {
      display: none;
    }
    margin: 0 auto;
    padding: 0;
    @media (max-width: ${breakpoint}) {
      grid-template-columns: auto 1fr;
    }
    @media (min-width: ${breakpoints.lg}) {
      justify-content: center;
    }
  `}
`;

export const HeaderBox = styled(Container)`
  display: flex;
  justify-content: center;
  position: relative;

  ${({ theme }) => css`
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: ${theme.spacing(2)};

    ${theme.media.sm.up} {
      margin-bottom: 0;
      align-items: center;
    }
  `}
`;

export const Header = styled(SectionHeader)`
  max-width: 369px;
  text-align: center;
  position: absolute;
  left: 32px;
  top: 50%;
  transform: translateY(-50%);
  @media (max-width: 1280px) {
    position: relative;
    left: 0;
  }
`;

export const FlexBox = styled(Flex)`
  justify-content: center;
  ${({ theme }) => css`
    ${theme.media.md.up} {
      width: 100%;
    }
  `}
`;
export const TextFlexBox = styled(Flex)`
  justify-content: center;
  width: 100%;
`;
export const InnerWrapper = styled.div<{
  $navQuickLinkBackgroundColor: string;
  $sticky?: boolean;
  $stickyTop?: number;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${px2rem(12)};
  margin: 0 auto;
  ${({
    $navQuickLinkBackgroundColor,
    theme: {
      colors,
      components: {
        header: { breakpoint, innerWrapper },
      },
    },
  }) => css`
    background: ${$navQuickLinkBackgroundColor ?? colors.white};
    @media (max-width: ${breakpoint}) {
      grid-template-columns: auto 1fr;
      padding: ${innerWrapper.paddingInlineMobile};
    }
    overflow-x: scroll;
    scrollbar-width: none; /* For Firefox */
    -ms-overflow-style: none; /* For Internet Explorer and Edge */
    &::-webkit-scrollbar {
      display: none;
    }
  `}
  ${({ $sticky, $stickyTop, theme }) => css`
    ${$sticky &&
    css`
      position: ${'sticky'};
      top: ${$stickyTop}px;
      z-index: ${theme.zIndex.navQuickLinks};
    `}
  `}
`;

export const Select = styled.select`
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-family: ${({ theme }) => theme.fontFamilies.openSans};
  font-size: 13px;
  font-weight: 700;
  height: 46px;
  padding-left: 16px;
  text-transform: uppercase;
  width: 100%;
  outline: none;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  z-index: 10;
`;

export const ArrowBox = styled(Box)`
  position: absolute;
  height: 100%;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 0 10px;
  z-index: 0;
  pointer-events: none;
`;
export const AccordionWrapper = styled(Accordion)`
  ${({ theme }) => css`
    font-size: 16px;
    line-height: 16px;
    & > header {
      & > button {
        padding: ${theme.spacing(1, 2)};
        line-height: 24px;
        .AccordionItemStyle {
          padding: 0;
          margin-bottom: 0;
        }
      }
    }
  `}
`;

export const AccordionItem = styled(Flex)<{
  $isOpen?: boolean;
  $isSvgImage: boolean;
}>`
  align-items: center;
  width: 100%;
  background-color: transparent;
  ${({ theme }) => css`
    padding: ${theme.spacing(1, 2)};
    margin-bottom: ${theme.spacing(0.5)};
  `}
  ${({ $isOpen, $isSvgImage }) =>
    $isOpen &&
    $isSvgImage &&
    css`
      img {
        filter: brightness(100);
      }
    `}
`;

export const AccordionBox = styled.div`
  border-radius: 100%;
  overflow: hidden;
  width: 24px;
  height: 24px;
  min-height: 24px;
  position: relative;
  ${({ theme }) => css`
    margin-right: ${theme.spacing(1)};
  `}
`;
export const Img = styled(NextImageWithLoader)`
  object-fit: cover;
  margin-bottom: -2px;
  ${({ theme }) => theme.media.md.up} {
    margin-bottom: 0;
  }
`;

export const StoresWrapper = styled(Swiper)`
  &&& {
    .swiper-slide {
      margin-right: 8px;
      max-width: 160px;
      min-width: 160px;
      ${({ theme }) => css`
        ${theme.media.md.up} {
          margin-right: 16px;
          max-width: 244px;
          min-width: 244px;
        }
      `}
    }
  }
`;
export const StoresWithImageWrapper = styled.div<{ $backgroundColor?: string }>`
  position: relative;
  .swiper {
    margin: 0 14px;
  }
  ${({ $backgroundColor, theme }) => css`
    background: ${$backgroundColor ? $backgroundColor : theme.colors.white};
  `}
`;
/**
 * The number after min-width is added to make the image long enough.
 * if the width is too short. This is so that the ui's animations don't go wrong.
 * Because left is for the image to reach the leftmost part of the container.
 *  */
export const RulerImage = styled.div<{ $width: number }>`
  background: url(${MobileRulerImage.src}) repeat-x;
  top: 18px;
  height: 18px;
  position: absolute;
  ${({ $width, theme }) => css`
    width: calc(${$width}px);
    min-width: 100vw;
    ${theme.media.md.up} {
      top: 13px;
      width: calc(${$width}px + 778px);
      min-width: calc(100vw + 1952px);
      background: url(${DesktopRulerImage.src}) repeat-x;
      left: -1274px;
    }
  `}
`;
export const SwiperBox = styled.div`
  overflow-x: scroll;
  overflow-y: hidden;
  position: relative;
  scroll-behavior: smooth;
  -ms-scroll-snap-type: x mandatory;
  scroll-snap-type: x mandatory;
  scrollbar-width: none; /* For Firefox */
  -ms-overflow-style: none; /* For Internet Explorer and Edge */
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ExhibitionsWithImageWrapper = styled.div<{
  $backgroundColor?: string;
}>`
  overflow: hidden;
  padding: 0 14px;
  position: relative;
  scroll-behavior: smooth;
  -ms-scroll-snap-type: x mandatory;
  scroll-snap-type: x mandatory;
  scrollbar-width: none; /* For Firefox */
  -ms-overflow-style: none; /* For Internet Explorer and Edge */
  &::-webkit-scrollbar {
    display: none;
  }
  height: 54px;
  display: flex;
  justify-content: start;
  align-items: center;
  ${({ $backgroundColor, theme }) => css`
    ${theme.media.md.up} {
      height: 80px;
    }
    background: ${$backgroundColor ? $backgroundColor : theme.colors.white};
  `}
`;

export const ExhibitionsRulerImage = styled.div`
  position: absolute;
  ${({ theme }) => css`
    background: url(${MobileCenterRulerImage}) repeat-x;
    min-width: 100vw;
    height: 24px;
    ${theme.media.md.up} {
      top: 26px;
      min-width: calc(110vw + 461px + 1150px);
      background: url(${DesktopCenterRulerImage}) repeat-x;
      left: -1150px;
    }
  `}
`;

export const ExhibitionsWrapper = styled(Swiper)`
  width: 100%;
  height: 100%;
  .swiper-wrapper {
    justify-content: flex-start;
    ${({ theme }) => theme.media.lg.up} {
      justify-content: center;
    }
  }
  .swiper-slide {
    margin-right: 0 !important;
    max-width: 130px;
    min-width: 130px;
    &:last-child {
      max-width: 131px;
      min-width: 131px;
    }
    ${({ theme }) => css`
      ${theme.media.md.up} {
        max-width: 288px;
        min-width: 288px;
        &:last-child {
          max-width: 288px;
          min-width: 288px;
        }
      }
    `}
  }
`;

export const VerticalWrapper = styled.div<{
  $footerHeight: number;
  $isSticky?: boolean;
  $mainHeight: number;
}>`
  ${({ $footerHeight, $mainHeight, theme }) => css`
    max-width: ${theme.breakpoints.lg};
    position: absolute;
    margin: 0 auto;
    min-height: ${$mainHeight - $footerHeight - 240}px;
    left: 0;
    right: 0;
  `}
  ${({ $isSticky }) =>
    $isSticky &&
    css`
      .vertical-stepper {
        top: 240px;
        left: auto;
        position: sticky;
      }
    `}
`;
